import { motion } from "framer-motion";
import {
  Target, Eye, ShieldCheck, Users, Leaf, Truck, Quote,
  Factory, Droplets, Wrench,
} from "lucide-react";
import { IraqMap } from "@/components/IraqMap";

const values = [
  { icon: ShieldCheck, title: "Quality & Innovation", desc: "Uncompromising commitment to international standards and continuous product refinement." },
  { icon: Users, title: "Customer Success", desc: "Tailored solutions and on-site technical assistance to maximise project efficiency." },
  { icon: Leaf, title: "Sustainability", desc: "Eco-conscious products that optimise cement usage and reduce environmental impact." },
  { icon: Truck, title: "Reliability & Reach", desc: "Seamless logistics and consistent supply across all regions of Iraq." },
];


const offerings = [
  { icon: Droplets, title: "Concrete Admixtures", desc: "High-range water reducers, polycarboxylate-based superplasticizers, slump retention agents, early-strength accelerators and retarders." },
  { icon: Factory, title: "Cement Grinding Aids", desc: "Performance enhancers designed to increase mill throughput and optimise cement strength development." },
  { icon: Wrench, title: "On-Site Technical Support", desc: "Concrete mix optimisation, aggregate analysis evaluation and custom formulation development for specific job-site demands." },
];

const Section = ({ eyebrow, title, children, className = "" }) => (
  <div className={className}>
    {eyebrow && <span className="text-xs uppercase tracking-widest font-mono text-brand-400">{eyebrow}</span>}
    {title && <h2 className="mt-3 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900">{title}</h2>}
    {children}
  </div>
);

export default function About() {
  return (
    <div className="bg-slate-50" data-testid="about-page">
      {/* HERO */}
      <section className="relative py-28 grain overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1764835711461-117d67799a7d?crop=entropy&cs=srgb&fm=jpg&w=1920&q=80" alt="Laboratory testing" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-50 via-slate-50/90 to-slate-50/60" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
          <span className="text-xs uppercase tracking-widest font-mono text-brand-400">About HMA</span>
          <h1 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 max-w-3xl leading-tight">
            Building Stronger Together
          </h1>
          <p className="mt-5 text-lg text-slate-600 max-w-2xl">
            Engineering high-performance concrete admixtures and cement grinding aids for Iraq's most demanding projects since 2009.
          </p>
        </div>
      </section>

      {/* EXECUTIVE SUMMARY */}
      <section className="py-20 max-w-7xl mx-auto px-5 sm:px-8 grid gap-14 lg:grid-cols-2 items-center">
        <Section eyebrow="Executive Summary" title="Who we are">
          <div className="mt-6 space-y-4 text-slate-600 leading-relaxed">
            <p>
              At HMA Construction Chemicals, we specialise in the engineering and production of high-performance
              concrete admixtures and cement grinding aids. Our journey began in <strong className="text-slate-900">2009 in Sulaymaniyah</strong>,
              Northern Iraq, operating with a single production line. Through technical innovation, uncompromised
              quality and reliable engineering solutions, we rapidly became a trusted market leader among major
              ready-mix concrete suppliers and cement plants throughout the region.
            </p>
            <p>
              To meet growing national infrastructure demands, we expanded in <strong className="text-slate-900">2018 with a
              state-of-the-art facility in Baghdad</strong> under the HMA brand. Guided by a mission to maintain the
              highest quality standards, HMA continues to innovate — anticipating future market expectations and
              delivering advanced chemical formulations tailored to local raw materials and site conditions.
            </p>
            <p>
              Today, with dual strategic manufacturing bases in <strong className="text-slate-900">Sulaymaniyah and Baghdad</strong>,
              HMA has established a robust distribution network covering key territories across Iraq.
            </p>
          </div>
        </Section>
        <motion.div initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}
          className="relative rounded-2xl overflow-hidden border border-slate-200 h-[420px]">
          <img src="https://images.unsplash.com/photo-1781330175070-6ec7e25f57aa?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80" alt="Scientific lab" className="w-full h-full object-cover" />
        </motion.div>
      </section>

      {/* VISION & MISSION */}
      <section className="py-20 bg-white/40 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid gap-6 md:grid-cols-2">
          {[
            { icon: Eye, title: "Our Vision", desc: "To be the premier partner and leading manufacturer of construction chemicals in Iraq and the region, driving the evolution of durable, efficient and sustainable concrete structures." },
            { icon: Target, title: "Our Mission", desc: "To empower our clients' success by providing high-performance admixtures, customised engineering solutions and exceptional technical support — fostering long-term, mutually beneficial relationships built on trust and value." },
          ].map((v, i) => (
            <motion.div key={v.title} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="p-8 rounded-xl bg-white border border-slate-200" data-testid={`vm-${i}`}>
              <div className="w-12 h-12 rounded-lg bg-brand-500/10 border border-brand-500/20 flex items-center justify-center text-brand-400"><v.icon size={22} /></div>
              <h3 className="mt-5 font-heading text-xl font-semibold text-slate-900">{v.title}</h3>
              <p className="mt-3 text-sm text-slate-500 leading-relaxed">{v.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CORE VALUES */}
      <section className="py-20 max-w-7xl mx-auto px-5 sm:px-8">
        <Section eyebrow="What drives us" title="Core Values" className="max-w-2xl" />
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {values.map((v, i) => (
            <motion.div key={v.title} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              className="p-7 rounded-xl bg-white border border-slate-200 hover:border-brand-500/50 transition-colors" data-testid={`value-${i}`}>
              <div className="w-12 h-12 rounded-lg bg-brand-500/10 border border-brand-500/20 flex items-center justify-center text-brand-400"><v.icon size={22} /></div>
              <h3 className="mt-5 font-heading text-lg font-semibold text-slate-900">{v.title}</h3>
              <p className="mt-2 text-sm text-slate-500 leading-relaxed">{v.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CEO MESSAGE */}
      <section className="py-20 bg-white/40 border-y border-slate-200">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <span className="text-xs uppercase tracking-widest font-mono text-brand-400">Leadership Message</span>
          <div className="mt-6 relative p-8 sm:p-10 rounded-2xl bg-white border border-slate-200">
            <Quote className="text-brand-500/40 absolute top-6 right-6" size={48} />
            <div className="space-y-4 text-slate-700 text-lg leading-relaxed font-heading">
              <p>
                "Our journey started in 2009 with a clear ambition: to bring world-class chemical technology to Iraq's
                construction industry. From a single line in Sulaymaniyah to our expanded state-of-the-art facility in
                Baghdad, HMA has grown into a cornerstone of quality for the country's most demanding projects.
              </p>
              <p>
                We believe that true success goes beyond business transactions. It is built on long-term partnerships,
                technical excellence and sustainable solutions that guarantee structural integrity for generations to come."
              </p>
            </div>
            <div className="mt-6 pt-6 border-t border-slate-200">
              <div className="font-heading font-semibold text-slate-900">HMA Leadership</div>
              <div className="text-sm text-brand-400 font-mono">Chief Executive Officer</div>
            </div>
          </div>
        </div>
      </section>

      {/* STRATEGIC PRESENCE */}
      <section className="py-20 max-w-7xl mx-auto px-5 sm:px-8">
        <Section eyebrow="Strategic Presence" title="Coverage across Iraq" className="max-w-2xl" />
        <p className="mt-4 text-slate-600 max-w-2xl">
          With primary manufacturing plants in Baghdad and Sulaymaniyah, HMA operates an integrated supply chain
          providing prompt delivery and dedicated technical support.
        </p>
        <div className="mt-10 grid gap-6 md:grid-cols-2">
          {[
            { city: "Sulaymaniyah Plant", year: "Since 2009", note: "Our founding manufacturing base in Northern Iraq." },
            { city: "Baghdad Plant", year: "Since 2018", note: "State-of-the-art facility serving central Iraq." },
          ].map((p) => (
            <div key={p.city} className="flex gap-4 p-6 rounded-xl bg-white border border-slate-200" data-testid={`plant-${p.city}`}>
              <div className="shrink-0 w-12 h-12 rounded-lg bg-brand-500/10 border border-brand-500/20 flex items-center justify-center text-brand-400"><Factory size={22} /></div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-heading font-semibold text-slate-900">{p.city}</h3>
                  <span className="text-xs font-mono px-2 py-0.5 rounded-full bg-gold-500/10 text-gold-400 border border-gold-500/20">{p.year}</span>
                </div>
                <p className="mt-1.5 text-sm text-slate-500">{p.note}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-10">
          <IraqMap />
        </div>
      </section>

      {/* OFFERINGS */}
      <section className="py-20 bg-white/40 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Section eyebrow="Products & Services" title="What we offer" className="max-w-2xl" />
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {offerings.map((o, i) => (
              <motion.div key={o.title} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="p-7 rounded-xl bg-white border border-slate-200" data-testid={`offering-${i}`}>
                <div className="w-12 h-12 rounded-lg bg-brand-500/10 border border-brand-500/20 flex items-center justify-center text-brand-400"><o.icon size={22} /></div>
                <h3 className="mt-5 font-heading text-lg font-semibold text-slate-900">{o.title}</h3>
                <p className="mt-2 text-sm text-slate-500 leading-relaxed">{o.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SUSTAINABILITY */}
      <section className="py-20 max-w-7xl mx-auto px-5 sm:px-8 grid gap-14 lg:grid-cols-2 items-center">
        <motion.div initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}
          className="relative rounded-2xl overflow-hidden border border-slate-200 h-[360px] order-2 lg:order-1">
          <img src="https://images.unsplash.com/photo-1653280679689-078c04c417a1?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80" alt="Sustainable concrete" className="w-full h-full object-cover" />
        </motion.div>
        <div className="order-1 lg:order-2">
          <span className="text-xs uppercase tracking-widest font-mono text-brand-400 inline-flex items-center gap-2"><Leaf size={13} /> Commitment to Sustainability</span>
          <h2 className="mt-3 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900">Efficient, eco-friendly concrete</h2>
          <p className="mt-6 text-slate-600 leading-relaxed">
            At HMA, we recognise that long-term success is inseparable from environmental stewardship. We integrate
            economic, social and environmental considerations into our research and product development.
          </p>
          <p className="mt-4 text-slate-600 leading-relaxed">
            Many of our advanced admixtures enable reduced water and cement consumption without sacrificing performance
            — contributing directly to more efficient, eco-friendly concrete applications across Iraq.
          </p>
        </div>
      </section>
    </div>
  );
}
